import { OnVariablesChanged, QueryModel, SearchModel, SortModel } from '../DataTableQuery.types';
import queryString, { ParseOptions } from 'query-string';
import { logger } from '../../../../../utils/logger';

export const searchModelMapper = {
  PARSE_SETTINGS: { arrayFormat: 'index', parseBooleans: true } as ParseOptions,

  toSearchString: <T extends Partial<Record<string, any>>>(
    params: T | undefined,
    arrayFormat: 'bracket' | 'index' | 'comma' | 'separator' | 'bracket-separator' | 'none' = 'index',
    excludeCustomParameters?: string[]
  ): string => {
    if (!params) {
      return '';
    }
    const result: Record<string, string | string[]> = {};
    Object.keys(params).forEach((key) => {
      if ((params[key] !== null || params[key] !== undefined) && !excludeCustomParameters?.includes(key)) {
        if (Array.isArray(params[key])) {
          result[key] = params[key].map((arr: any) => JSON.stringify(arr));
        } else if (typeof params[key] === 'object' && params[key] instanceof Date) {
          result[key] = new Date(params[key]).toISOString();
        } else if (typeof params[key] === 'object') {
          result[key] = JSON.stringify(params[key]);
        } else {
          result[key] = `${params[key]}`;
        }
      }
    });
    return queryString.stringify(result, { arrayFormat: arrayFormat || 'bracket' });
  },
  fromSearchString: <Q extends QueryModel, S extends SortModel>(
    searchParams: string | null | undefined
  ): SearchModel<Q, S> => {
    try {
      const parsedValues = queryString.parse(searchParams ?? '', searchModelMapper.PARSE_SETTINGS) as {
        query?: string;
        sort?: string[] | string;
        quickReportId?: string;
        [key: string]: unknown;
      };
      // Sometimes queryString parse undefined to 'undefined' (string)
      if (parsedValues.query === 'undefined') parsedValues.query = undefined;
      if (parsedValues.sort === 'undefined') parsedValues.sort = undefined;
      if (parsedValues.quickReportId === 'undefined') parsedValues.quickReportId = undefined;
      const parsedQuery = JSON.parse(parsedValues?.query || '{}') as OnVariablesChanged<Q, S>['query'];
      const parsedSort = searchModelMapper.parseArrayValues<Q, S>(parsedValues?.sort || []);
      // get customParams. For now page is ignored in params
      const { query, sort, page, quickReportId, ...customParams } = parsedValues;
      const parsedCustomParams = searchModelMapper.parseCustomParams(customParams);

      const onlyDataTableValues = {
        query: Object.keys(parsedQuery || {}).length > 0 ? parsedQuery : undefined,
        sort: parsedSort && parsedSort.length > 0 ? parsedSort : undefined,
        quickReportId,
        ...parsedCustomParams
      };
      return searchModelMapper.cleanParams(onlyDataTableValues);
    } catch (e) {
      logger.warn(`Cannot convert params: `, e);
      return {};
    }
  },
  cleanParams: <Q extends QueryModel, S extends SortModel>(
    params: OnVariablesChanged<Q, S> & { quickReportId?: string }
  ) => {
    type VarKeyOf = keyof OnVariablesChanged<Q, S>;
    Object.keys(params).forEach((key) => {
      params[key as VarKeyOf] === undefined && delete params[key as VarKeyOf];
    });
    return Object.keys(params).reduce((acc, key) => {
      if (params[key as VarKeyOf] === undefined) return acc;
      return { ...acc, [key]: params[key as VarKeyOf] };
    }, {});
  },
  parseCustomParams: (params: { [key: string]: unknown } = {}) => {
    try {
      return Object.keys(params).reduce((acc, key) => {
        const paramValue = params[key];
        if (paramValue === undefined || typeof paramValue !== 'string') return acc;
        return { ...acc, [key]: paramValue };
      }, {});
    } catch (e) {
      logger.warn(`Cannot convert custom params: `, e);
      return {};
    }
  },
  parseArrayValues: <Q extends QueryModel, S extends SortModel>(
    arr: string[] | string
  ): OnVariablesChanged<Q, S>['sort'] => {
    if (!Array.isArray(arr)) return [];
    return arr.map((a) => {
      try {
        return JSON.parse(a);
      } catch (e) {
        return a;
      }
    });
  }
};
